import React, { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import styled from 'styled-components';

import {
  deriveBestEffortTimeline,
  mapBackendTimeline,
  ITimelineEvent,
  TimelineCategory,
} from 'core/utils/studentTimeline';

import { IStudent } from './types';
import { StudentColors } from './studentColors';

interface IActivityTimelineProps {
  student: IStudent;
}

/** Category → semantic token for the event dot (Req 16.7). */
const CATEGORY_COLOR: Record<TimelineCategory, string> = {
  signed: StudentColors.accent,
  paid: StudentColors.success,
  moved: StudentColors.neutral,
  terminated: StudentColors.danger,
  invoiceIssued: StudentColors.warn,
  invoiceLinked: StudentColors.success,
};

/**
 * Activity timeline for the Student card (Area 6, Req 16; TZ-3).
 *
 * Renders the canonical backend feed (`student.timeline`) when present, mapped
 * by `mapBackendTimeline`; otherwise falls back to the best-effort client-
 * derived feed (`deriveBestEffortTimeline`) and shows a «best-effort» note. Each
 * event shows a localized label, its date, and a category-derived dot; a
 * localized «no activity» state renders when empty (Req 16.4). Labels via
 * i18next, category colors from `StudentColors` tokens.
 */
export const ActivityTimeline = memo(({ student }: IActivityTimelineProps) => {
  const { t } = useTranslation();

  const backendEdges = student.timeline?.edges;
  const usingBackendFeed = Boolean(backendEdges && backendEdges.length > 0);

  const events: ITimelineEvent[] = useMemo(
    () =>
      usingBackendFeed
        ? mapBackendTimeline(backendEdges)
        : deriveBestEffortTimeline(student),
    [usingBackendFeed, backendEdges, student],
  );

  return (
    <Card>
      <Title>{t('pages.studentDetail.timeline.title')}</Title>
      {!usingBackendFeed ? (
        <Hint>{t('pages.studentDetail.timeline.bestEffort')}</Hint>
      ) : null}

      {events.length === 0 ? (
        <Empty>{t('pages.studentDetail.timeline.empty')}</Empty>
      ) : (
        <List>
          {events.map((event) => (
            <Item key={event.id}>
              <Dot color={CATEGORY_COLOR[event.category]} aria-hidden="true" />
              <Body>
                <Label>{t(event.labelKey, event.meta)}</Label>
                <Date>{moment(event.date).format('DD.MM.YYYY')}</Date>
              </Body>
            </Item>
          ))}
        </List>
      )}
    </Card>
  );
});

const Card = styled.div`
  margin-top: 12px;
  padding: 14px 16px;
  border: 1px solid ${StudentColors.border};
  border-radius: 10px;
  background: ${StudentColors.surface};
`;

const Title = styled.div`
  font-size: 13px;
  font-weight: 700;
  color: ${StudentColors.textPrimary};
`;

const Hint = styled.div`
  font-size: 11px;
  color: ${StudentColors.textMuted};
  margin: 2px 0 12px;
`;

const Empty = styled.div`
  font-size: 13px;
  color: ${StudentColors.textMuted};
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const Item = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const Dot = styled.span<{ color: string }>`
  width: 9px;
  height: 9px;
  border-radius: 50%;
  flex: 0 0 auto;
  background: ${({ color }) => color};
`;

const Body = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 12px;
  width: 100%;
`;

const Label = styled.span`
  font-size: 13px;
  color: ${StudentColors.textSecondary};
`;

const Date = styled.span`
  font-size: 12px;
  color: ${StudentColors.textMuted};
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
`;
